// Reveal — run an entrance animation when a section scrolls into view.
//
// The landing's marketing sections sit below the fold, so a load-time
// animation has already finished by the time anyone scrolls to them — the page
// would animate for nobody. This waits for the section to actually enter the
// viewport, applies the animation class ONCE, and disconnects.
//
// Reduced motion needs no branch here: the animation classes it applies are
// defined inside `prefers-reduced-motion: no-preference` in globals.css, so
// for a reduced-motion visitor the class lands and simply does nothing —
// content is visible throughout, never hidden waiting for an effect.

import { useEffect, useRef, useState, type ReactNode } from 'react';

export interface RevealProps {
  readonly children: ReactNode;
  /** Animation class to apply on entry — `anim-stagger` or `anim-rise`. */
  readonly animation?: 'anim-stagger' | 'anim-rise';
  readonly className?: string;
}

export function Reveal({ children, animation = 'anim-stagger', className }: RevealProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el === null) return undefined;
    // No IntersectionObserver (old browser, some test environments): show
    // everything immediately rather than never animating anything in.
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      // Fire when ~15% is visible — early enough that the motion is seen,
      // late enough that it is not already over by the time the eye arrives.
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={[className, inView ? animation : ''].filter(Boolean).join(' ')}>
      {children}
    </div>
  );
}
